import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/marketing/Navbar";
import Footer from "@/components/marketing/Footer";
import { Button } from "@/components/ui/button";
import { AIValueLabel } from "@/components/shared";
import { track } from "@/lib/api";
import {
  ArrowRight, PencilLine, GitCompare, TrendingUp, LineChart,
  Building2, Users, Cpu, FileBarChart, AlertTriangle, CheckCircle2, Gauge,
} from "lucide-react";

const STEPS = [
  { icon: PencilLine, title: "Track or import the work", body: "Track work in NetLift, import it from a spreadsheet, send it through the API, or connect a supported tool." },
  { icon: GitCompare, title: "Compare time with and without AI", body: "Record how long the same work would have taken without AI, so the saving is measured instead of assumed." },
  { icon: TrendingUp, title: "Connect cost to return", body: "Apply staff and AI costs to calculate current net value, future value, and payback." },
  { icon: LineChart, title: "Decide what happens next", body: "Know what to expand, review, improve, or stop, with Evidence Quality attached to each claim." },
];

const USE_CASES = [
  { icon: Building2, t: "Measure Marketing's AI Return" },
  { icon: Cpu, t: "Compare Copilot, ChatGPT, Claude, Gemini, and other AI tools" },
  { icon: Users, t: "Decide which AI licences to renew or expand" },
  { icon: AlertTriangle, t: "Find workflows where AI is wasting time" },
  { icon: LineChart, t: "Forecast AI rollout costs and future value" },
  { icon: FileBarChart, t: "Report AI value to finance and leadership" },
];

const QUESTIONS = [
  "Where is AI saving time?",
  "Which AI tools are worth renewing, expanding, or removing?",
  "Which workflows are costing more than they return?",
  "What value could repeatable AI-assisted work create over 3, 6, and 12 months?",
  "What is the payback period?",
  "Which AI value claims can leadership report with confidence?",
  "Are we measuring P&L impact or capacity value?",
];

const LABEL_INPUTS = ["Time with AI", "Time without AI", "Staff cost", "AI tool cost", "Repeatable future savings", "Evidence source"];
const LABEL_OUTPUTS = ["Time Saved", "Current Net Value", "Future Value", "Payback", "Evidence Quality", "Next Action"];

const TRUST_POINTS = [
  ["Time Saved", "See whether AI actually reduced work time."],
  ["Current Net Value", "Connect time saved to commercial value."],
  ["Future Value", "Forecast repeatable savings."],
  ["Payback", "See how long it may take for AI savings to cover the related cost."],
  ["Evidence Quality", "Separate estimates from stronger evidence."],
  ["Next Action", "Know what to expand, review, improve, or stop."],
];

export default function Home() {
  const navigate = useNavigate();
  useEffect(() => {
    track("homepage_viewed");
  }, []);

  const goSignup = () => { track("primary_cta_clicked", { from: "home" }); navigate("/signup"); };
  const goDemo = () => { track("sample_report_clicked", { from: "home" }); navigate("/demo"); };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-ink/10 bg-[#FFF8E9]">
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 py-14 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:py-20">
          <div className="max-w-2xl">
            <span className="inline-flex items-center rounded-full border border-editorial/20 bg-editorial/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-editorial">
              AI Value Management
            </span>
            <h1 className="mt-6 font-heading text-4xl font-semibold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
              Save Time.<br />Show Return.
            </h1>
            <div className="mt-6 max-w-lg space-y-3">
              <p className="text-lg font-medium leading-relaxed text-slate-700">
                AI Value Management for measuring the cost, savings, and return of AI adoption.
              </p>
              <p className="text-base leading-relaxed text-slate-600">
                Compare AI-assisted work with the time it would have taken without AI.
              </p>
              <p className="text-base font-medium leading-relaxed text-slate-700">
                See what to expand, review, improve, or stop.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button size="lg" className="bg-editorial hover:bg-editorial/90" onClick={goSignup} data-testid="hero-primary-cta">
                Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" className="border-ink/20" onClick={goDemo} data-testid="hero-secondary-cta">
                See Sample Report
              </Button>
            </div>
            <p className="mt-3 text-sm text-slate-500">14-day free trial. No credit card required. Use sample data or your own.</p>
          </div>

          <div className="lg:pl-6">
            <AIValueLabel
              title="AI Value Label"
              verified
              fields={[
                { label: "Time Saved", value: "+1.5 hrs", tone: "positive" },
                { label: "Current Net Value", value: "+$112", tone: "positive" },
                { label: "Future Value", value: "+$840", tone: "ink" },
                { label: "Evidence Quality", value: "Moderate Confidence", tone: "editorial" },
                { label: "Payback", value: "2 mo", tone: "ink" },
                { label: "Spend Status", value: "On track", tone: "muted" },
              ]}
              status="expand"
              nextAction="Expand what works."
              testid="hero-value-label"
            />
            <p className="mt-3 text-center text-xs text-slate-400">One label for AI time, cost, return, evidence, and next action.</p>
          </div>
        </div>
      </section>

      {/* Measure More Than Usage */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <div className="grid items-center gap-10 md:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-editorial">Measure more than usage</p>
            <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-ink">AI Usage Is Not AI Value.</h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              Most AI dashboards show activity, users, seats, or prompts. NetLift shows whether
              AI-assisted work is saving time, reducing cost, creating future value, or costing more
              than it returns.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Track or import the work. Compare time with and without AI. Show the return.
            </p>
          </div>
          <div className="rounded-xl border border-ink/10 bg-white p-6 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Questions NetLift helps answer</p>
            <ul className="mt-4 space-y-3 text-sm text-slate-700">
              {QUESTIONS.map((q) => (
                <li key={q} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#1E9E6A]" />
                  <span>{q}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Compare Time With and Without AI */}
      <section className="border-y border-ink/10 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <h2 className="font-heading text-3xl font-semibold tracking-tight text-ink">Compare Time With and Without AI.</h2>
          <p className="mt-2 text-base text-slate-500">Track or import the work. Compare time with and without AI. Show the return.</p>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <div key={s.title} className="rounded-xl border border-ink/10 bg-background p-6">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-editorial text-white">
                  <s.icon className="h-5 w-5" />
                </div>
                <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-slate-400">Step {i + 1}</p>
                <h3 className="mt-1 font-heading text-lg font-medium text-ink">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How the AI Value Label Works */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-ink text-white"><Gauge className="h-5 w-5" /></span>
          <h2 className="font-heading text-3xl font-semibold tracking-tight text-ink">How the AI Value Label Works</h2>
        </div>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-600">
          NetLift brings together the key inputs needed to understand whether AI-assisted work is
          creating measurable value, then turns them into a simple label leaders can read and act on.
        </p>
        <p className="mt-4 max-w-3xl text-base font-medium leading-relaxed text-ink">
          Most AI ROI reports show a number. NetLift also shows how reliable that number is.
        </p>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <div className="rounded-xl border border-ink/10 bg-white p-7">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Inputs NetLift considers</p>
            <ul className="mt-4 space-y-2.5 text-sm text-slate-700">
              {LABEL_INPUTS.map((x) => (
                <li key={x} className="flex items-start gap-2"><ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-editorial" /> {x}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-editorial/20 bg-editorial/[0.03] p-7">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-editorial">Outputs NetLift shows</p>
            <ul className="mt-4 space-y-2.5 text-sm text-slate-700">
              {LABEL_OUTPUTS.map((x) => (
                <li key={x} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#1E9E6A]" /> {x}</li>
              ))}
            </ul>
          </div>
        </div>
        <p className="mt-6 max-w-3xl text-sm leading-relaxed text-slate-500">
          NetLift does not treat every AI value claim equally. Evidence Quality helps separate stronger
          evidence from estimates or incomplete data, so teams can understand how much confidence to
          place in each result.
        </p>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-500">
          Replace one-off AI ROI spreadsheets with repeatable value reporting.
        </p>
        <div className="mt-7 flex flex-wrap items-center gap-3">
          <Button className="bg-editorial hover:bg-editorial/90" onClick={goDemo} data-testid="value-label-sample-cta">
            See Sample Report <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button variant="outline" className="border-ink/20" onClick={goSignup} data-testid="value-label-cta">
            Start Free Trial
          </Button>
        </div>
      </section>

      {/* See AI Return Across the Business */}
      <section className="border-t border-ink/10 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <h2 className="font-heading text-3xl font-semibold tracking-tight text-ink">Measure AI Value Across Teams, Tools, and Workflows.</h2>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-slate-600">
            Use NetLift before AI renewals, seat expansions, and wider rollouts to see what is worth keeping.
          </p>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {USE_CASES.map((u) => (
              <div key={u.t} className="flex items-start gap-4 rounded-xl border border-ink/10 bg-background p-5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-slate-100 text-navy">
                  <u.icon className="h-5 w-5" />
                </span>
                <p className="text-sm font-medium text-slate-700">{u.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations teaser */}
      <section className="border-y border-ink/10 bg-background">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-2xl">
              <h2 className="font-heading text-3xl font-semibold tracking-tight text-ink">Bring Your AI Work Data In.</h2>
              <p className="mt-3 text-base leading-relaxed text-slate-600">
                Capture work in NetLift, import it from CSV, send records through the API, or connect a
                supported tool using your own credentials. Every integration is clearly labelled by its
                current status.
              </p>
            </div>
            <Button variant="outline" className="border-ink/20" onClick={() => { track("integrations_cta_clicked", { from: "home" }); navigate("/integrations"); }} data-testid="home-integrations-cta">
              See integrations &amp; API <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          <div className="mt-8 space-y-4">
            {[
              ["Live now", "live", ["Native Time Tracker", "CSV import", "Inbound API"]],
              ["Connect with your credentials", "available", ["Microsoft Entra ID", "Microsoft 365 Copilot", "ChatGPT Workspace", "NetSuite Finance", "Jira", "Azure DevOps", "OpenAI usage and cost"]],
              ["In beta", "beta", ["Okta / Auth0", "Google Workspace", "Anthropic / Claude usage"]],
              ["Planned or coming soon", "planned", ["Zendesk", "QuickBooks", "GitHub Copilot metrics", "Gemini", "Desktop Timer"]],
            ].map(([group, state, items]) => {
              const cls = {
                live: "bg-[#1E9E6A]/10 text-[#1E9E6A] border-[#1E9E6A]/25",
                available: "bg-aqua/20 text-[#1d6e72] border-aqua/40",
                beta: "bg-gold/20 text-[#8a6d12] border-gold/50",
                planned: "bg-slate-100 text-slate-500 border-slate-200",
              }[state];
              return (
                <div key={group} className="flex flex-wrap items-center gap-2.5">
                  <span className="w-full text-xs font-semibold uppercase tracking-widest text-slate-400 sm:w-56">{group}</span>
                  {items.map((name) => (
                    <span key={name} className={`rounded-full border px-3 py-1 text-xs font-medium ${cls}`}>{name}</span>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust — leads with reliable AI value reporting */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <div className="max-w-2xl">
          <h2 className="font-heading text-3xl font-semibold tracking-tight text-ink">AI value reporting your leadership team can trust.</h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600">
            NetLift helps teams measure AI-assisted work against time, cost, future value, and return,
            with Evidence Quality showing how reliable each value claim is.
          </p>
          <p className="mt-3 text-base font-medium leading-relaxed text-ink">
            Usage is useful input. Value is the decision.
          </p>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {TRUST_POINTS.map(([t, d]) => (
            <div key={t} className="rounded-xl border border-ink/10 bg-white p-5">
              <p className="font-heading text-sm font-semibold text-ink">{t}</p>
              <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{d}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 max-w-3xl rounded-xl border border-ink/10 bg-white p-5" data-testid="home-pl-capacity-block">
          <p className="font-heading text-sm font-semibold text-ink">Separate P&amp;L impact from capacity value</p>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
            See which benefits affect the P&amp;L and which represent time freed up for other work.
          </p>
        </div>
      </section>

      {/* Trust — built for AI investment decisions */}
      <section className="border-t border-ink/10 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <div className="max-w-3xl">
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-ink">Built for AI Investment Decisions, Not Employee Surveillance.</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              NetLift measures value across work, workflows, tools, projects, and business outcomes. No
              screen tracking. No keystroke tracking. No hidden browser monitoring. No individual
              productivity scoring.
            </p>
          </div>
          <div className="mt-8 max-w-3xl rounded-xl border border-ink/10 bg-background p-5" data-testid="home-faq-monitoring">
            <p className="text-sm font-semibold text-ink">Is NetLift employee monitoring software?</p>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
              No. NetLift measures the value of AI-assisted work, not individual activity. It does not
              use screen tracking, keystroke tracking, hidden browser monitoring, or individual
              productivity scoring.
            </p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-ink">
        <div className="mx-auto max-w-7xl px-5 py-16 text-center sm:px-8">
          <h2 className="font-heading text-3xl font-semibold tracking-tight text-white">Know What to Expand, Review, Improve, or Stop.</h2>
          <p className="mx-auto mt-3 max-w-xl text-base text-white/70">Expand what works. Review what is unclear. Improve what underperforms. Stop what wastes time.</p>
          <p className="mx-auto mt-2 max-w-xl text-base text-white/70">See where AI creates value, where the evidence is weak, and what should happen next.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button size="lg" className="bg-white text-ink hover:bg-slate-100" onClick={goSignup} data-testid="cta-bottom-signup">
              Start Free Trial
            </Button>
            <Button size="lg" variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10" onClick={goDemo}>
              See Sample Report
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
